import React, { Fragment } from "react"

import { Handle, Position } from "reactflow"

import CloseIcon from "@mui/icons-material/Close"
import { Card, Grid, IconButton } from "@mui/material"
import { Typography } from "@synapse-analytics/synapse-ui"

import { getNodeHandleXPosition } from "../../../utils/workflowHelpers"

import styles from "../Nodes.module.scss"

export interface BaseWorkflowNodeProps {
  id: string
  menuView?: boolean
  readMode?: boolean
  editMode?: boolean
  nodeType: string
  title?: string
  iconProps: {
    element: React.ReactElement
    className: string
    width?: string
  }
  onRemove?: () => void
  children: React.ReactNode
  className?: string
  width?: number
  handlesProps?: {
    showTarget?: boolean
    showSource?: boolean
    sources?: Array<{ id: string; position: number }>
  }
  customActions?: React.ReactNode
  headerAction?: React.ReactNode
}

/**
 * BaseWorkflowNode provides a consistent structure for all workflow nodes.
 * Handles common UI patterns, handles, styling, and layout.
 * Reduces duplication across all node components by ~200 lines each.
 */
export function BaseWorkflowNode(props: Readonly<BaseWorkflowNodeProps>): React.ReactElement {
  const {
    menuView = false,
    readMode = false,
    title,
    iconProps,
    onRemove,
    children,
    className = "",
    width,
    handlesProps,
    customActions,
    headerAction,
  } = props

  const cardClassName = `${styles.card} ${className} nodrag`
  const cardStyle = width ? { width: `${width}px` } : undefined

  const shouldRenderCloseButton = !menuView && !readMode && onRemove

  return (
    <Card className={cardClassName} style={cardStyle}>
      {/* Target Handle */}
      {!menuView && (handlesProps?.showTarget ?? true) && (
        <Handle style={{ opacity: 0 }} type="target" position={Position.Top} id="b" isConnectable={true} />
      )}

      <Grid container justifyContent="space-between">
        {/* Node Logo Section */}
        <Grid item width={iconProps.width ?? "40px"}>
          <Grid item className={styles[iconProps.className]} width={"100%"} display="flex">
            {iconProps.element}
          </Grid>
        </Grid>

        {/* Node Content Section */}
        <Grid container item xs paddingLeft={1} gap={1}>
          {/* Node Header */}
          {(title || shouldRenderCloseButton || headerAction) && (
            <Grid item xs={12} container justifyContent="space-between">
              <Grid item xs>
                <Typography variant="label-bold" variantColor={2}>
                  {title}
                </Typography>
              </Grid>

              {headerAction && headerAction}

              {/* Remove Button */}
              {!menuView && !readMode && onRemove && (
                <Grid item width={"25px"} justifySelf="flex-end">
                  <IconButton style={{ marginTop: "-15px" }} size="small" onClick={onRemove}>
                    <CloseIcon fontSize="small" className={styles.remove} />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          )}

          {/* Node Body */}
          <Grid container item xs={12} gap={1} alignItems="space-between" justifyContent="flex-end">
            <Grid item xs={12} display="flex" flexDirection="column">
              {children}
            </Grid>

            {/* Custom Actions */}
            {customActions}
          </Grid>
        </Grid>
      </Grid>

      {/* Source Handles */}
      {!menuView && (handlesProps?.showSource ?? true) && (
        <Fragment>
          {handlesProps?.sources && handlesProps.sources.length > 0 ? (
            handlesProps.sources.map((handle, index) => (
              <Handle
                key={handle.id}
                style={{
                  left: `${getNodeHandleXPosition(index, width as number, handlesProps?.sources?.length as number)}px`,
                  opacity: 0,
                }}
                type="source"
                position={Position.Bottom}
                id={handle.id}
                isConnectable={true}
              />
            ))
          ) : (
            <Handle style={{ opacity: 0 }} type="source" position={Position.Bottom} id="a" isConnectable={true} />
          )}
        </Fragment>
      )}
    </Card>
  )
}
